import gridReadContract from '../../../utils/gridReadContract.json';
import type { GridPageFilters } from '../../../types/grid';
import type { IServerSideDatasource } from 'ag-grid-community';
import type { MutableRefObject } from 'react';
import type { 
  CRMFilterModel, 
  CRMFilterCondition,
  ServerSideCache
} from '../../../types/grid';
import type { DynamicRecord } from '../../../types/api';
import type { QueryConditions } from '../../../types/supabase';
import type { BasicFilterCondition, FilterOperator, JoinFilterCondition } from '../../../types/filters';
import { getFieldDependentTable } from '../../../utils/metadata';
import { clientForSchema, fieldDependentRelation } from '../../../utils/metadata/tables';
import { applyFilters } from '../../../utils/filter';
import {
  MODE_GROUP_CAP,
  columnStatPlan,
  columnStatSelect,
  formatColumnStat,
  type ColumnStatPlan,
  type ColumnStatResult,
} from '../../../utils/columnStats';
import type { EnrichedTableField } from '../../../types/metadata';
import { toast as message } from 'sonner';
import { expandMultiFilterModel, isSetFilterModel, setFilterQueryCondition, type GridSetFilterModel } from '../../../utils/gridFilterModels';

/**
 * Parameters for filter adjustment functions
 */
export interface FilterAdjustmentParams {
  metadata: {
    relationshipLookupData?: any;
    hashes?: {
      fieldDataHash?: string;
      relationshipLookupDataHash?: string;
      tableDataHash?: string;
    };
  };
  tableName: string;
}

/**
 * Parameters for datasource creation
 */
export interface DataSourceParams {
  tableName: string;
  schemaName: string;
  source?: 'view' | 'deepView' | 'base';
  topLevelFilters?: GridPageFilters;
  searchFilterRef?: MutableRefObject<CRMFilterCondition | null>;
  searchConditionsRef?: MutableRefObject<QueryConditions | null>;
  searchColumn?: string;
  searchColumns?: readonly string[];
  controlledData?: DynamicRecord[];
  metadata: FilterAdjustmentParams['metadata'];
  clientSideRowsRef: MutableRefObject<DynamicRecord[]>;
  serverSideCacheRef: MutableRefObject<ServerSideCache>;
  /**
   * REG-664: expanded relationship columns (`*_ref`) currently shown by the grid.
   * They are omitted from the select unless requested, so a table with an
   * expanded relationship does not pay for the target's whole field list on
   * every page. A ref rather than a value so toggling column visibility does not
   * rebuild the datasource.
   */
  visibleRefColumnsRef?: MutableRefObject<string[]>;
}

/**
 * Adjusts filter model for relationship fields
 */
export function adjustFilterModelForRelationships(
  filterModel: CRMFilterModel,
  params: FilterAdjustmentParams
): CRMFilterModel {
  const relationshipsByField = params.metadata?.relationshipLookupData?.[params.tableName]?.byFieldName || {};
  const adjustedFilterModel = { ...filterModel };

  Object.entries(filterModel).forEach(([modelKey, filter]) => {
    const fieldName = filter.colId || modelKey;
    const relationship = relationshipsByField[fieldName];
    if (!relationship) return;

    delete adjustedFilterModel[modelKey];
    if (isSetFilterModel(filter)) {
      // Checkbox choices carry the stable foreign-key IDs.
      adjustedFilterModel[modelKey] = { ...filter, colId: fieldName };
      return;
    }

    // Typed relationship searches operate on the label rendered by the grid.
    adjustedFilterModel[modelKey] = {
      ...filter,
      colId: `${fieldName}_rel.label`,
    };
  });

  return adjustedFilterModel;
}

/**
 * Adjusts filter model for range filters (date and numeric)
 */
export function adjustFilterModelForRangeFilters(filterModel: CRMFilterModel): CRMFilterModel {
  const adjustedFilterModel = { ...filterModel };

  Object.entries(filterModel).forEach(([fieldName, filter]) => {
    const columnId = filter.colId || fieldName;
    // Handle AND operator structure with multiple conditions
    if (filter.operator === 'AND' && filter.conditions) {
      delete adjustedFilterModel[fieldName];
      
      filter.conditions.forEach((condition, index) => {
        const conditionFieldName = index === 0 ? fieldName : `${fieldName}__${index}`;
        let processedCondition = { ...condition, colId: columnId };
        
        if (condition.filterType === 'date') {
          if (condition.dateFrom) {
            processedCondition.filter = condition.dateFrom;
          }
        }
        
        adjustedFilterModel[conditionFieldName] = processedCondition;
      });
      return;
    }

    // Handle date filters
    if (filter.filterType === 'date') {
      switch (filter.type) {
        case 'equals':
        case 'notEqual':
        case 'lessThan':
        case 'greaterThan':
          if (filter.dateFrom) {
            adjustedFilterModel[fieldName] = {
              ...adjustedFilterModel[fieldName],
              filter: filter.dateFrom
            };
          }
          break;
        
        case 'inRange':
          delete adjustedFilterModel[fieldName];
          
          if (filter.dateFrom) {
            adjustedFilterModel[`${fieldName}__gte`] = {
              colId: columnId,
              filterType: 'date',
              type: 'greaterThanOrEqual',
              filter: filter.dateFrom
            };
          }
          
          if (filter.dateTo) {
            adjustedFilterModel[`${fieldName}__lte`] = {
              colId: columnId,
              filterType: 'date',
              type: 'lessThanOrEqual',
              filter: filter.dateTo
            };
          }
          break;
        
        case 'blank':
        case 'notBlank':
          break;
        
        default:
          if (filter.dateFrom) {
            adjustedFilterModel[fieldName] = {
              ...adjustedFilterModel[fieldName],
              filter: filter.dateFrom
            };
          }
          break;
      }
    }
    // Handle numeric filters
    else if (filter.filterType === 'number' && filter.type === 'inRange') {
      delete adjustedFilterModel[fieldName];
      
      if (filter.filter !== null && filter.filter !== undefined) {
        adjustedFilterModel[`${fieldName}__gte`] = {
          colId: columnId,
          filterType: 'number',
          type: 'greaterThanOrEqual',
          filter: filter.filter
        };
      }
      
      if (filter.filterTo !== null && filter.filterTo !== undefined) {
        adjustedFilterModel[`${fieldName}__lte`] = {
          colId: columnId,
          filterType: 'number',
          type: 'lessThanOrEqual',
          filter: filter.filterTo
        };
      }
    }
  });

  return adjustedFilterModel;
}

/**
 * Adjusts order by clause for relationship fields
 */
export function adjustOrderByForRelationships(
  orderBy: { column: string; direction: 'ascending' | 'descending' },
  params: FilterAdjustmentParams
): { column: string; direction: 'ascending' | 'descending' } {
  const relationshipsByField = params.metadata?.relationshipLookupData?.[params.tableName]?.byFieldName || {};
  const adjustedOrderBy = { ...orderBy };

  const relationship = relationshipsByField[orderBy.column];
  if (relationship) {
    adjustedOrderBy.column = `${relationship.col_name}_rel.label`;
  }
  return adjustedOrderBy;
}

/**
 * Converts CRM filter model to query conditions
 */
export function convertFilterModelToQueryConditions(
  adjustedFilterModel: CRMFilterModel
): QueryConditions {
  const queryConditions: QueryConditions = {};

  Object.entries(adjustedFilterModel).forEach(([fieldName, filter]) => {
    let colId = filter.colId || fieldName;

    if (colId.includes('.')) {
      const parts = colId.split('.');
      colId = `${parts[0]}->>${parts[1]}`;
    }
    
    // Convert CRMFilterCondition to BasicFilterCondition filterType
    let queryFilterType: BasicFilterCondition['filterType'] = 'text';
    switch (filter.filterType) {
      case 'number':
      case 'agNumberColumnFilter':
        queryFilterType = 'number';
        break;
      case 'date':
      case 'agDateColumnFilter':
        queryFilterType = 'date';
        break;
      case 'text':
      case 'agTextColumnFilter':
      case 'set':
      case 'agSetColumnFilter':
      default:
        queryFilterType = 'text';
        break;
    }
    
    // Handle set filters specially
    if (filter.filterType === 'set' || filter.filterType === 'agSetColumnFilter') {
      const condition = setFilterQueryCondition(filter as GridSetFilterModel, queryFilterType, colId);
      if (condition) queryConditions[fieldName] = condition;
      return;
    }
    
    // Map CRM filter types to FilterOperator types
    let queryType: FilterOperator = 'equals';
    switch (filter.type) {
      case 'equals':
        queryType = 'equals';
        break;
      case 'contains':
        queryType = 'contains';
        break;
      case 'startsWith':
        queryType = 'startsWith';
        break;
      case 'greaterThan':
        queryType = 'greaterThan';
        break;
      case 'lessThan':
        queryType = 'lessThan';
        break;
      case 'greaterThanOrEqual':
        queryType = 'greaterThanOrEqual';
        break;
      case 'lessThanOrEqual':
        queryType = 'lessThanOrEqual';
        break;
      case 'notEqual':
        queryType = 'notEqual';
        break;
      case 'blank':
        queryType = 'blank';
        break;
      case 'notBlank':
        queryType = 'notBlank';
        break;
      case 'plain_fts':
        queryType = 'plain_fts';
        break;
      default:
        queryType = 'equals';
        break;
    }
    
    // Create a BasicFilterCondition
    const filterCondition: BasicFilterCondition = {
      filterType: queryFilterType,
      type: queryType,
      colId: colId,
      filter: filter.filter
    };
    
    queryConditions[fieldName] = filterCondition;
  });

  return queryConditions;
}

/**
 * Builds the full set of query conditions a grid is currently showing: its column filters
 * plus the page-level filters and the table search box.
 *
 * Used both by the datasource (per block request) and by live updates (to decide whether a
 * row changed by another user still belongs in this grid).
 */
export function buildGridQueryConditions(params: {
  filterModel: CRMFilterModel;
  topLevelFilters?: GridPageFilters;
  searchFilter?: CRMFilterCondition | null;
  searchConditions?: QueryConditions | null;
  searchColumn?: string;
  searchColumns?: readonly string[];
  metadata: any;
  tableName: string;
}): QueryConditions {
  const {
    filterModel,
    topLevelFilters,
    searchFilter,
    searchConditions,
    searchColumn = 'full_text_search',
    searchColumns,
    metadata,
    tableName,
  } = params;

  const filterAdjustmentParams: FilterAdjustmentParams = { metadata, tableName };

  let adjustedFilterModel = expandMultiFilterModel(filterModel) as CRMFilterModel;
  adjustedFilterModel = adjustFilterModelForRelationships(adjustedFilterModel, filterAdjustmentParams);
  adjustedFilterModel = adjustFilterModelForRangeFilters(adjustedFilterModel);

  const groupConditions: QueryConditions = {};
  if (topLevelFilters) {
    Object.entries(topLevelFilters).forEach(([key, value]) => {
      if (value.filterType === 'join') groupConditions[key] = value;
      else adjustedFilterModel[key] = value;
    });
  }

  const queryConditions = { ...convertFilterModelToQueryConditions(adjustedFilterModel), ...groupConditions };

  if (searchFilter) {
    const configuredColumns = searchColumns?.length ? searchColumns : [searchColumn];
    if (configuredColumns.length === 1) {
      queryConditions[configuredColumns[0]] = {
        ...searchFilter,
        colId: configuredColumns[0],
      } as BasicFilterCondition;
    } else {
      queryConditions.__table_search = {
        filterType: 'join',
        type: 'OR',
        conditions: configuredColumns.map((column) => ({
          ...searchFilter,
          colId: column,
        } as BasicFilterCondition)),
      } satisfies JoinFilterCondition;
    }
  }

  if (searchConditions) {
    Object.assign(queryConditions, searchConditions);
  }

  return queryConditions;
}

/**
 * Creates a server-side datasource for AG Grid
 */
export function createServerSideDataSource(params: DataSourceParams): IServerSideDatasource {
  const {
    tableName,
    schemaName,
    source,
    topLevelFilters = {},
    searchFilterRef,
    searchConditionsRef,
    searchColumn,
    searchColumns,
    controlledData,
    metadata,
    clientSideRowsRef,
    serverSideCacheRef,
    visibleRefColumnsRef
  } = params;
  return {
    getRows: async (gridParams) => {
      // Handle controlled data mode
      if (controlledData) {
        gridParams.success({
          rowData: controlledData,
          rowCount: controlledData.length,
        });
        return;
      }

      try {
        const { startRow = 0, endRow, filterModel, sortModel } = gridParams.request;
        const requestedRowCount = Math.max(
          1,
          (endRow ?? (startRow + gridParams.api.paginationGetPageSize())) - startRow,
        );
        const limitOffset = {
          // buildQueryForFieldDependentTable uses an inclusive range end.
          limit: requestedRowCount - 1,
          offset: startRow,
        };

        // Default sorting
        const orderBy: { column: string; direction: 'ascending' | 'descending' } = {
          column: gridReadContract.sortColumn,
          direction: gridReadContract.sortDirection as 'descending',
        };

        // Apply sort model if provided
        if (sortModel.length > 0) {
          orderBy.column = sortModel[0].colId;
          orderBy.direction = sortModel[0].sort === 'asc' ? 'ascending' : 'descending';
        }

        // Create filter adjustment parameters
        const filterAdjustmentParams: FilterAdjustmentParams = {
          metadata,
          tableName
        };

        const adjustedOrderBy = adjustOrderByForRelationships(
          {
            column: orderBy.column,
            direction: orderBy.direction as 'ascending' | 'descending'
          },
          filterAdjustmentParams
        );

        const queryConditions = buildGridQueryConditions({
          filterModel: filterModel as CRMFilterModel,
          topLevelFilters,
          searchFilter: searchFilterRef?.current ?? null,
          searchConditions: searchConditionsRef?.current ?? null,
          searchColumn,
          searchColumns,
          metadata,
          tableName
        });

        const { data: rows, error, count } = await getFieldDependentTable({
          tableName,
          schema: schemaName,
          conditions: queryConditions,
          orderBy: adjustedOrderBy as { column: string; direction: 'ascending' | 'descending' },
          limitOffset,
          withCount: true,
          source,
          refColumns: visibleRefColumnsRef?.current,
        });

        if (error) {
          console.error('Error fetching data:', error);
          const errorMessage = error.message ? `: ${error.message}` : '';
          message.error(`Failed to fetch data${errorMessage}.`);
          gridParams.fail();
        } else {
          const safeRows = Array.isArray(rows) ? rows : (rows ? [rows] : []);
          const safeCount = count || 0;
          serverSideCacheRef.current = { rows: safeRows, count: safeCount };
          const allRows = [...clientSideRowsRef.current, ...safeRows];
          const totalRowCount = safeCount + clientSideRowsRef.current.length;
          gridParams.success({
            rowData: allRows,
            rowCount: totalRowCount,
          });
        }
      } catch (error) {
        console.error('Error in datasource getRows:', error);
        gridParams.fail();
      }
    },
  };
}

/**
 * REG-802: how a table grid column is summarised in its column menu.
 * Relationships report the most common related label; picklists their most
 * common value. Calculated fields get no stat.
 */
export function tableColumnStatPlan(
  field: EnrichedTableField | undefined,
  isPicklist: boolean,
): ColumnStatPlan | null {
  if (!field?.name || field.name.endsWith('_ref') || field.name === 'full_text_search') return null;
  // A calculated field is computed per row, and some (merchant.deal_status_calc)
  // take longer than the statement timeout across every merchant.
  if (field.is_calculated) return null;
  return columnStatPlan({
    column: field.name,
    dataType: String(field.data_type ?? ''),
    isCurrency: Boolean(field.ui_is_currency),
    isPercentage: Boolean(field.ui_is_percentage),
    precision: field.ui_scale ?? null,
    labelColumn: field.relationship ? `${field.name}_rel->>label` : undefined,
    categorical: isPicklist,
  });
}

/**
 * REG-802: one column's aggregate over every row the grid's filters match
 * (column filters, page filters and search), computed by PostgREST.
 */
export async function fetchTableColumnStat({
  tableName,
  schemaName,
  source,
  conditions,
  plan,
}: {
  tableName: string;
  schemaName: string;
  source?: 'view' | 'deepView' | 'base';
  conditions: QueryConditions;
  plan: ColumnStatPlan;
}): Promise<ColumnStatResult> {
  let query = (clientForSchema(schemaName) as any)
    .from(fieldDependentRelation(tableName, source))
    .select(columnStatSelect(plan, 'id'));
  query = applyFilters(query, conditions);
  if (plan.kind === 'mode') query = query.limit(MODE_GROUP_CAP);
  const { data, error } = await query;
  if (error) throw error;
  return formatColumnStat(plan, data ?? []);
}
